import { Home, Library, Compass, Inbox, type LucideIcon } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useResearchHubAccess } from '@/hooks/useSurfaceAccess';
import { useResearchCapability } from '@/hooks/useResearchCapability';
import { useNavbarPermissions } from '@/hooks/useNavbarPermissions';

// Visibility for the 5-tab mobile IA (P0-IA-03). Reproduces ALL THREE live gating mechanisms the
// old PartnerNavbar used, so the bottom bar shows exactly what each role may reach:
//   (a) useNavbarPermissions.canAccess  — team-scoped navbar_sections/navbar_permissions
//   (b) useResearchCapability / useResearchHubAccess — research surface RPCs (read/author + cio/pm/analyst)
//   (c) inline role_level / team         — admin bypass, investment-team checks
// The centre Capture FAB is not a tab; it is gated separately on the 'author' capability.

export interface MobileTab {
  key: 'home' | 'library' | 'hub' | 'inbox';
  label: string;
  to: string;
  icon: LucideIcon;
}

export interface TabVisibility {
  authed: boolean;
  loading: boolean;
  tabs: MobileTab[];
  showCapture: boolean;
  isAdmin: boolean;
  /** team-scoped navbar gate, exposed for future per-section needs (e.g. desktop deep-links). */
  canAccess: (section: string) => boolean;
}

export function useTabVisibility(): TabVisibility {
  const { user, staffMember } = useAuth();
  const role = staffMember?.role_level ?? null;
  const team = staffMember?.team ?? null;

  const nav = useNavbarPermissions(role, team);
  const hub = useResearchHubAccess();
  const author = useResearchCapability('author');

  const authed = !!user;
  const tabs: MobileTab[] = [];

  if (authed) {
    tabs.push({ key: 'home', label: 'Home', to: '/partner/m/home', icon: Home });
    tabs.push({ key: 'library', label: 'Library', to: '/partner/document-library', icon: Library });
    if (hub.canRead || hub.isAdmin) {
      tabs.push({ key: 'hub', label: 'Hub', to: '/partner/research-hub', icon: Compass });
    }
    tabs.push({ key: 'inbox', label: 'Inbox', to: '/partner/m/inbox', icon: Inbox });
  }

  return {
    authed,
    loading: nav.loading || hub.loading || author.isLoading,
    tabs,
    showCapture: authed && (Boolean(author.data) || hub.isAdmin),
    isAdmin: hub.isAdmin,
    canAccess: nav.canAccess,
  };
}
